import { api, idempotencyHeaders } from './client';

// ----- Dev (will be removed when real auth ships) ---------------------------
export const devListUsers = () => api.get('/api/dev/users').then((r) => r.data);
export const devSeed = () => api.post('/api/dev/seed').then((r) => r.data);
export const devSetPassword = (userId, password) => api.post(`/api/dev/users/${userId}/set-password`, { password }).then((r) => r.data);

// ----- Current user ---------------------------------------------------------
export const fetchMe = () => api.get('/api/me').then((r) => r.data);
export const updateProfile = (body) => api.patch('/api/me/profile', body).then((r) => r.data);
export const setWallets = (body) => api.put('/api/me/wallets', body).then((r) => r.data);

// ----- Proof of Work (member-side transparency feed) ------------------------
// Returns the SAME shape as /api/admin/proof-of-work/selections but gated to
// member auth so any logged-in user can verify the random engine for themselves.
export const fetchMemberProofOfWorkSelections = () => api.get('/api/proof-of-work/selections').then((r) => r.data);

// ----- Wallet ---------------------------------------------------------------
// Patch v18.0 — /api/me/swap endpoint returns HTTP 410 Gone. The client-side
// helper is preserved as a stub that throws immediately so any legacy caller
// gets an unambiguous signal without a network round-trip.
export const swap = () => {
  return Promise.reject(new Error('Self-exchange between purchase_balance and withdrawal_balance was removed in Patch v18.0.'));
};
export const withdraw = (amount, asset, destination_address) =>
  api
    .post(
      '/api/me/withdraw',
      {
        amount: String(amount),
        asset,
        destination_address: destination_address || undefined,
      },
      { headers: idempotencyHeaders() },
    )
    .then((r) => r.data);

// ----- Purchases ------------------------------------------------------------
export const buyPosition = (fund_source = 'purchase_balance') =>
  api.post('/api/me/positions/buy', { fund_source }, { headers: idempotencyHeaders() }).then((r) => r.data);
export const buySubpack = (tier_price_usd, fund_source = 'purchase_balance') =>
  api.post('/api/me/subpacks/buy', { tier_price_usd, fund_source }, { headers: idempotencyHeaders() }).then((r) => r.data);
export const getMySubPacks = () => api.get('/api/me/subpacks').then((r) => r.data);
// Live Sub Pack tier ladder w/ operator-saved intervals. Member-auth
// gated. Powers the "Pick a Tier" dropdown so the displayed cadence
// matches what the backend will actually snapshot at purchase time.
// Returns { tiers: [{tier_price_usd, total_positions, release_interval_hours}, ...] }
export const fetchSubpackTiers = () => api.get('/api/me/subpacks/tiers').then((r) => r.data);

// ----- PIF & Adoption -------------------------------------------------------
export const pif = (target_user_id, item, fund_source = 'purchase_balance') =>
  api.post('/api/me/pif', { target_user_id, item, fund_source }, { headers: idempotencyHeaders() }).then((r) => r.data);
export const adopt = (orphan_user_id, item, fund_source = 'purchase_balance') =>
  api.post('/api/adopt', { orphan_user_id, item, fund_source }, { headers: idempotencyHeaders() }).then((r) => r.data);

// ----- Referrals ------------------------------------------------------------
export const listReferrals = (page = 1) => api.get('/api/me/referrals', { params: { page } }).then((r) => r.data);

// ----- Stats + live tracker -------------------------------------------------
export const getStats = (range = 'all') => api.get('/api/me/stats', { params: { range } }).then((r) => r.data);
export const getLiveTracker = () => api.get('/api/me/live-tracker').then((r) => r.data);
export const getPoolStatus = () => api.get('/api/pool/status').then((r) => r.data);
export const getProfitSummary = () => api.get('/api/me/profit-summary').then((r) => r.data);
export const getPositionStats = () => api.get('/api/me/position-stats').then((r) => r.data);

// ----- 1SC3 Ad Credits + Banner Marketplace --------------------------------
export const getMyAdCredits = () => api.get('/api/me/banner-ads/credits').then((r) => r.data);
export const listMyBanners = (page = 1) => api.get('/api/me/banner-ads', { params: { page } }).then((r) => r.data);
export const createBanner = (payload) => api.post('/api/me/banner-ads', payload).then((r) => r.data);
export const pauseBanner = (bannerId) => api.patch(`/api/me/banner-ads/${bannerId}/pause`).then((r) => r.data);
export const resumeBanner = (bannerId) => api.patch(`/api/me/banner-ads/${bannerId}/resume`).then((r) => r.data);

// ----- Public rotation (used by BannerAdSlot on Home + Member area) --------
export const getBannerRotation = (count = 2) => api.get('/api/public/banner-rotation', { params: { count } }).then((r) => r.data);
export const recordBannerImpressions = (ad_ids) => api.post('/api/public/banner-impressions', { ad_ids }).then((r) => r.data);
export const recordBannerClick = (bannerId) => api.post(`/api/public/banner-click/${bannerId}`).then((r) => r.data);

// ----- Pre-Launch Mode (homepage countdown + purchase-page banner) --------
// Anonymous-readable, cached 5s server-side. Powers the countdown rendered
// between banners and the intro video on Home, and the disabled-buy banner
// on the member Purchase subpage. Failing to fetch is non-fatal — callers
// default to "not in pre-launch" when the response is missing.
export const fetchPublicLaunchStatus = () => api.get('/api/public/launch-status').then((r) => r.data);

// ----- Ledger ---------------------------------------------------------------
export const listLedger = (page = 1, type) => api.get('/api/me/ledger', { params: { page, type } }).then((r) => r.data);

// ----- Support tickets ------------------------------------------------------
export const listTickets = (page = 1) => api.get('/api/me/tickets', { params: { page } }).then((r) => r.data);
export const createTicket = (subject, message) => api.post('/api/me/tickets', { subject, message }).then((r) => r.data);
export const replyToTicket = (ticketId, message) => api.post(`/api/me/tickets/${ticketId}/messages`, { message }).then((r) => r.data);

// ----- Global pool (Patch v17.5 — P2P transfers permanently removed) ------
export const searchUsers = (username) => api.get('/api/users/search', { params: { username } }).then((r) => r.data);
// NOTE: `transferP2P` was intentionally removed in Patch v17.5. The
// backend now responds to POST /api/transfers with HTTP 410 Gone. Any
// residual caller in the codebase must be updated; see the DEPLOYMENT
// guide for the migration path (use /api/adopt for orphan onboarding).
export const listGlobalPool = (page = 1) => api.get('/api/global-pool', { params: { page } }).then((r) => r.data);

// ---------- Payments: NOWPayments live deposits (Step 6E) -----------------
//
// Switched from one-shot invoices to PERMANENT per-user sub-partner
// addresses. ``fetchLiveDepositAddresses`` returns the SAME on-chain
// address every visit (lazy-minted on first call). User sends any amount
// from external wallet \u2014 NOWPayments credits via IPN as a repeat deposit.
// Read-only check: does the user already have minted addresses?
// Never mints. Use this to decide whether to show the "Request Wallet
// Address" CTA (no addresses yet) or auto-display the existing addresses.
export const getExistingLiveDepositAddresses = () => api.get('/api/payments/deposits/addresses').then((r) => r.data);

export const fetchLiveDepositAddresses = () => api.post('/api/payments/deposits/initiate').then((r) => r.data);

// Per-currency on-demand mint. Used by the "Request [LTC] Address" CTAs.
// Idempotent — re-clicking after the address is minted returns the same
// address without burning a NOWPayments API call.
export const mintSingleDepositAddress = (currency) => api.post(`/api/payments/deposits/mint/${encodeURIComponent(currency)}`).then((r) => r.data);

export const getLiveDepositStatus = (paymentId) => api.get(`/api/payments/deposits/${encodeURIComponent(paymentId)}`).then((r) => r.data);

export const getPaymentsConfig = () => api.get('/api/payments/config').then((r) => r.data);

// ----- Getting Started (3-step onboarding) ---------------------------------
export const getOnboardingState = () => api.get('/api/me/getting-started').then((r) => r.data);
export const searchSponsorCandidates = (q) => api.get('/api/me/getting-started/search-sponsors', { params: { q } }).then((r) => r.data);
export const verifySponsor = () => api.post('/api/me/getting-started/verify-sponsor').then((r) => r.data);
export const changeSponsor = (username) => api.post('/api/me/getting-started/change-sponsor', { username }).then((r) => r.data);

// ---------- Patch v16: Public Economic Config -----------------------------
// Anonymous-readable endpoint exposing the operator-controlled
// auto-repurchase divert %. Returns
//   { auto_repurchase_divert_ratio: "0.20",
//     auto_repurchase_divert_ratio_pct: 20.0 }
// Used by DualWalletCards (and the Cycle Proof of Work explanation copy)
// so the displayed % tracks any admin change automatically.
export const fetchPublicEconomicConfig = () => api.get('/api/public/economic-config').then((r) => r.data);

// ---------- Patch v16: Cycle Proof of Work --------------------------------
// Member-facing endpoints powering the new "Cycle Proof of Work" subpage.
// All require authentication.

// Patch v17.4 — Pending Positions. Paginated 1-based. Filterable by phase.
// Each row: { position_id, phase: "filler"|"root", source, purchase_created_at }
// Response also includes filter_counts: { all, filler, root } reflecting
// the member's FULL pending set regardless of the active filter, so the UI
// can render badge counts alongside the filter chips.
export const fetchCycleProofPending = (page = 1, perPage = 50, phase = 'all') =>
  api
    .get('/api/me/cycle-pow/pending', {
      params: { page, per_page: perPage, phase },
    })
    .then((r) => r.data);

// List A — Personal Cycles. Paginated 1-based. Each row: { position_id,
// cycled, cycled_at, purchase_created_at, source, auto_rebuy_status,
// claimable_rebuys, retry_count, retry_completed_via }
export const fetchCycleProofPersonal = (page = 1, perPage = 50) =>
  api
    .get('/api/me/cycle-pow/personal', {
      params: { page, per_page: perPage },
    })
    .then((r) => r.data);

// List B — Referral Matching Bonus. Paginated 1-based. Each row:
// { position_id, sponsored_user_id, sponsored_username, amount_usd,
//   status: "paid"|"failed"|"processing"|"abandoned",
//   event_at, completed_via, claimable, retry_count }
export const fetchCycleProofReferrals = (page = 1, perPage = 50) =>
  api
    .get('/api/me/cycle-pow/referrals', {
      params: { page, per_page: perPage },
    })
    .then((r) => r.data);

// "Claim Rebuys" — manually trigger the 2 missing auto-rebuy positions
// for a cycle whose inline snowball failed. Idempotent.
// Returns { status: "ok", via: "manual_claim", position_id } on success.
// 403 NOT_OWNER, 404 NO_ROW_TO_PROCESS, 409 ALREADY_PROCESSED on failure.
export const claimCycleRebuys = (positionId) => api.post('/api/me/cycle-pow/claim-rebuys', { position_id: positionId }).then((r) => r.data);

// "Process Bonus" — manually credit a sponsor matching bonus whose inline
// payout failed. Applies the live auto-repurchase divert %. Idempotent.
// Returns { status: "ok", via: "manual_claim", position_id } on success.
export const processCycleBonus = (positionId) => api.post('/api/me/cycle-pow/process-bonus', { position_id: positionId }).then((r) => r.data);
